// File Name: Navbar.jsx

import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  Menu,
  X,
} from "lucide-react";

/* LOGO */
import logo from "@/assets/companylogo.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {

  const [scrolled, setScrolled] =
    useState(false);

  const [open, setOpen] =
    useState(false);

  const location = useLocation();

  useEffect(() => {

    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    onScroll();

    window.addEventListener(
      "scroll",
      onScroll
    );

    return () =>
      window.removeEventListener(
        "scroll",
        onScroll
      );

  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>

      {/* ===================================================== */}
      {/* NAVBAR */}
      {/* ===================================================== */}

      <motion.header

        initial={{ y: -80 }}

        animate={{ y: 0 }}

        transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1],
        }}

        className={`
          fixed
          top-0
          left-0
          right-0

          z-50

          transition-all
          duration-500

          ${
            scrolled
              ? `
                bg-white/75
                backdrop-blur-2xl

                border-b
                border-purple-200/40

                shadow-[0_10px_50px_rgba(120,70,180,0.12)]
              `
              : `
                bg-white/35
                backdrop-blur-xl
              `
          }
        `}
      >

        {/* TOP GLOW */}

        <div
          className="
            absolute inset-0

            bg-gradient-to-r
            from-purple-500/[0.03]
            via-transparent
            to-fuchsia-500/[0.04]

            pointer-events-none
          "
        />

        {/* MOBILE SHADOW */}

        <div
          className="
            absolute
            inset-x-0
            bottom-0

            h-px

            bg-gradient-to-r
            from-transparent
            via-purple-300/40
            to-transparent
          "
        />

        <nav
          className="
            relative

            max-w-7xl
            mx-auto

            px-4 sm:px-6 md:px-8

            flex items-center justify-between

            h-[74px]
            sm:h-20
          "
        >

          {/* ===================================================== */}
          {/* LOGO */}
          {/* ===================================================== */}

          <Link
            to="/"

            className="
              flex items-center
              group

              shrink-0
            "
          >

            <div
              className="
                relative

                flex items-center justify-center
              "
            >

              {/* PURPLE GLOW */}

              <div
                className="
                  absolute

                  inset-0

                  bg-purple-500/10

                  blur-3xl

                  rounded-full

                  scale-[1.5]

                  opacity-80

                  group-hover:opacity-100

                  transition-all duration-500
                "
              />

              {/* LOGO */}

              <img
                src={logo}
                alt="CodeFix Solutions"

                className="
                  relative

                  h-12
                  sm:h-16
                  md:h-20
                  lg:h-24

                  w-auto

                  object-contain

                  drop-shadow-[0_8px_25px_rgba(120,70,180,0.18)]

                  transition-all duration-300

                  group-hover:scale-[1.04]
                "
              />

            </div>

          </Link>

          {/* ===================================================== */}
          {/* DESKTOP NAVIGATION */}
          {/* ===================================================== */}

          <div
            className="
              hidden md:flex

              items-center

              gap-1 lg:gap-2
            "
          >

            {links.map((l) => (

              <NavLink
                key={l.to}

                to={l.to}

                end={l.to === "/"}

                className={({ isActive }) =>
                  `
                    relative

                    px-4 lg:px-5
                    py-2.5

                    text-sm lg:text-[15px]

                    font-semibold

                    tracking-wide

                    rounded-xl

                    transition-all duration-300

                    ${
                      isActive
                        ? `
                          text-purple-700
                        `
                        : `
                          text-slate-700
                          hover:text-purple-700
                        `
                    }
                  `
                }
              >

                {({ isActive }) => (
                  <>

                    {l.label}

                    {isActive && (

                      <motion.div

                        layoutId="nav-indicator"

                        className="
                          absolute

                          inset-0

                          rounded-xl

                          bg-purple-100/70

                          border
                          border-purple-200/60

                          -z-10
                        "

                        transition={{
                          type: "spring",
                          bounce: 0.25,
                          duration: 0.6,
                        }}
                      />

                    )}

                  </>
                )}

              </NavLink>

            ))}

          </div>

          {/* ===================================================== */}
          {/* CTA */}
          {/* ===================================================== */}

          <div className="hidden md:block">

            <Link
              to="/contact"

              className="
                group

                inline-flex
                items-center
                justify-center

                px-6 lg:px-7
                py-3.5

                rounded-2xl

                bg-gradient-to-r
                from-[#6D3B8C]
                via-[#9B4D96]
                to-[#B86BC5]

                text-white
                text-sm
                font-semibold

                shadow-xl
                shadow-purple-500/20

                hover:scale-[1.03]
                active:scale-[0.98]

                hover:shadow-purple-500/35

                transition-all duration-300
              "
            >

              Book Consultation

            </Link>

          </div>

          {/* ===================================================== */}
          {/* MOBILE MENU BUTTON */}
          {/* ===================================================== */}

          <button

            className="
              md:hidden

              relative

              p-2.5 sm:p-3

              rounded-2xl

              text-slate-900

              bg-white/60
              backdrop-blur-md

              border border-purple-200/40

              hover:bg-purple-50

              active:scale-[0.95]

              transition-all duration-300
            "

            onClick={() =>
              setOpen((prev) => !prev)
            }
          >

            <AnimatePresence mode="wait">

              <motion.div
                key={open ? "close" : "menu"}

                initial={{
                  opacity: 0,
                  rotate: -90,
                  scale: 0.8,
                }}

                animate={{
                  opacity: 1,
                  rotate: 0,
                  scale: 1,
                }}

                exit={{
                  opacity: 0,
                  rotate: 90,
                  scale: 0.8,
                }}

                transition={{
                  duration: 0.2,
                }}
              >

                {open ? (
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                )}

              </motion.div>

            </AnimatePresence>

          </button>

        </nav>

      </motion.header>

      {/* ===================================================== */}
      {/* MOBILE MENU */}
      {/* ===================================================== */}

      <AnimatePresence>

        {open && (

          <motion.div

            initial={{
              opacity: 0,
              y: -15,
            }}

            animate={{
              opacity: 1,
              y: 0,
            }}

            exit={{
              opacity: 0,
              y: -15,
            }}

            transition={{
              duration: 0.25,
            }}

            className="
              fixed

              top-[74px]
              sm:top-20

              inset-x-0

              z-40

              md:hidden

              px-4
              pt-2
            "
          >

            <div
              className="
                overflow-hidden

                rounded-[28px]

                bg-white/85
                backdrop-blur-2xl

                border
                border-purple-200/40

                shadow-[0_20px_60px_rgba(120,70,180,0.12)]
              "
            >

              {/* INNER GLOW */}

              <div
                className="
                  absolute inset-0

                  bg-gradient-to-br
                  from-purple-500/[0.03]
                  via-transparent
                  to-fuchsia-500/[0.05]

                  pointer-events-none
                "
              />

              <div
                className="
                  relative

                  px-4 sm:px-6
                  py-5 sm:py-6

                  flex flex-col

                  gap-2 sm:gap-3
                "
              >

                {links.map((l) => (

                  <NavLink
                    key={l.to}

                    to={l.to}

                    end={l.to === "/"}

                    className={({ isActive }) =>
                      `
                        px-5
                        py-3.5

                        rounded-2xl

                        text-sm
                        sm:text-base

                        font-medium

                        transition-all duration-300

                        ${
                          isActive
                            ? `
                              bg-purple-100
                              text-purple-700
                              border border-purple-200
                            `
                            : `
                              text-slate-700
                              hover:bg-purple-50
                            `
                        }
                      `
                    }
                  >

                    {l.label}

                  </NavLink>

                ))}

                {/* MOBILE CTA */}

                <Link
                  to="/contact"

                  className="
                    mt-3

                    inline-flex
                    items-center
                    justify-center

                    px-6
                    py-4

                    rounded-2xl

                    bg-gradient-to-r
                    from-[#6D3B8C]
                    via-[#9B4D96]
                    to-[#B86BC5]

                    text-white
                    text-sm
                    sm:text-base

                    font-semibold

                    shadow-lg
                    shadow-purple-500/20

                    active:scale-[0.98]

                    transition-all duration-300
                  "
                >

                  Book Consultation

                </Link>

              </div>

            </div>

          </motion.div>

        )}

      </AnimatePresence>

    </>
  );
}